import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Printer, Trash2, Send, Check, X as XIcon, FileText, Edit, ArrowRight } from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import Navbar from '../../components/Navbar';
import QuotationForm from '../../components/sales/QuotationForm';
import api from '../../lib/api';

const getErrorMessage = (error) => {
  if (!error.response) return error.message || 'Network error occurred';
  const { status, data } = error.response;
  if (data?.detail) {
    if (Array.isArray(data.detail)) {
      const first = data.detail[0];
      if (typeof first === 'object') {
        return `${first.loc?.[first.loc.length - 1] || 'Field'}: ${first.msg || 'Invalid value'}`;
      }
      return String(first);
    }
    return typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail);
  }
  return `Request failed with status ${status}`;
};

const statusBadge = (status) => {
  const map = {
    draft: 'bg-[#F5F5F5] text-[#6b7280]',
    sent: 'bg-[#DBEAFE] text-[#1E40AF]',
    accepted: 'bg-[#DBEAFE] text-[#1E40AF]',
    rejected: 'bg-[#FEE2E2] text-[#991B1B]',
    converted: 'bg-[#F3E8FF] text-[#6B21A8]',
  };
  return map[status] || 'bg-[#F5F5F5] text-[#6b7280]';
};

const Quotations = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingQuotation, setEditingQuotation] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    if (!token || !storedUser) {
      navigate('/login');
      return;
    }
    setUser(JSON.parse(storedUser));
    fetchQuotations();

  }, [navigate, statusFilter]);

  const fetchQuotations = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      const response = await api.get(`/quotations?${params}`);
      setQuotations(response.data);
    } catch (err) {
      console.error('Error fetching quotations:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async (q) => {
    if (!window.confirm(`Send quotation ${q.quotation_number} to the customer?`)) return;
    try {
      await api.post(`/quotations/${q.id}/send`);
      fetchQuotations();
    } catch (err) {
      alert(`Failed to send: ${getErrorMessage(err)}`);
    }
  };

  const handleAccept = async (q) => {
    if (!window.confirm(`Mark quotation ${q.quotation_number} as accepted?`)) return;
    try {
      await api.post(`/quotations/${q.id}/accept`);
      fetchQuotations();
    } catch (err) {
      alert(`Failed to accept: ${getErrorMessage(err)}`);
    }
  };

  const handleReject = async (q) => {
    if (!window.confirm(`Reject quotation ${q.quotation_number}?`)) return;
    try {
      await api.post(`/quotations/${q.id}/reject`);
      fetchQuotations();
    } catch (err) {
      alert(`Failed to reject: ${getErrorMessage(err)}`);
    }
  };

  const handleConvert = async (q) => {
    if (
      !window.confirm(
        'Convert this quotation to a confirmed invoice? The invoice will be created immediately.'
      )
    )
      return;
    try {
      const res = await api.post(`/quotations/${q.id}/convert-to-invoice`);
      const inv = res.data;
      alert(`Invoice ${inv.invoice_number} created successfully`);
      navigate(`/sales/invoices/${inv.id}/print`);
    } catch (err) {
      alert(`Failed to convert: ${getErrorMessage(err)}`);
    }
  };

  const handleEdit = async (q) => {
    try {
      const res = await api.get(`/quotations/${q.id}`);
      setEditingQuotation(res.data);
      setShowForm(true);
    } catch (err) {
      alert(`Failed to load quotation: ${getErrorMessage(err)}`);
    }
  };

  const handleDelete = async (q) => {
    if (!window.confirm(`Delete quotation ${q.quotation_number}?`)) return;
    try {
      await api.delete(`/quotations/${q.id}`);
      fetchQuotations();
    } catch (err) {
      alert(`Failed to delete: ${getErrorMessage(err)}`);
    }
  };

  const handleViewInvoice = async (q) => {
    try {
      const res = await api.get(`/quotations/${q.id}/converted-invoice`);
      window.open(`/sales/invoices/${res.data.id}/print`, '_blank');
    } catch (err) {
      alert(`Could not find converted invoice: ${getErrorMessage(err)}`);
    }
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingQuotation(null);
    fetchQuotations();
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingQuotation(null);
  };

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  return (
    <div className="flex h-screen bg-[#f0f2f5]" data-testid="quotations-page">
      <Sidebar isOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />

      <div
        className={`flex-1 flex flex-col transition-all duration-300 ${
          isSidebarOpen ? 'md:ml-64' : 'md:ml-0'
        }`}
      >
        <Navbar user={user} toggleSidebar={toggleSidebar} />

        <main className="flex-1 overflow-y-auto p-6 sm:p-8">
          <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <h1
              className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#1a1a2e]"
              data-testid="quotations-title"
            >
              Quotations
            </h1>
            <button
              onClick={() => {
                setEditingQuotation(null);
                setShowForm(true);
              }}
              className="bg-gradient-to-r from-[#22B8D4] via-[#4A7AE0] to-[#7C3BD6] text-white rounded-lg px-4 py-2 font-medium transition-colors flex items-center gap-2"
              data-testid="add-quotation-button"
            >
              <Plus className="w-5 h-5" />
              New Quotation
            </button>
          </div>

          <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm mb-6 p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#1a1a2e] mb-2">
                  Status Filter
                </label>
                <select
                  className="w-full border border-[#e5e7eb] rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#4A7AE0]/20 focus:border-[#4A7AE0]"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  data-testid="status-filter"
                >
                  <option value="">All Statuses</option>
                  <option value="draft">Draft</option>
                  <option value="sent">Sent</option>
                  <option value="accepted">Accepted</option>
                  <option value="rejected">Rejected</option>
                  <option value="converted">Converted</option>
                </select>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">Loading...</div>
          ) : quotations.length === 0 ? (
            <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm p-12 text-center">
              <p className="text-[#6b7280] mb-4">No quotations yet</p>
              <button
                onClick={() => {
                  setEditingQuotation(null);
                  setShowForm(true);
                }}
                className="bg-gradient-to-r from-[#22B8D4] via-[#4A7AE0] to-[#7C3BD6] text-white rounded-lg px-4 py-2 font-medium transition-colors"
              >
                Create Your First Quotation
              </button>
            </div>
          ) : (
            <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm overflow-x-auto">
              <table className="w-full text-sm text-left" data-testid="quotations-table">
                <thead className="bg-[#f9fafb] text-[#6b7280] font-medium border-b border-[#e5e7eb]">
                  <tr>
                    <th className="py-3 px-4">Quotation #</th>
                    <th className="py-3 px-4">Customer</th>
                    <th className="py-3 px-4">Issue Date</th>
                    <th className="py-3 px-4">Expiry</th>
                    <th className="py-3 px-4">Total</th>
                    <th className="py-3 px-4">Status</th>
                    <th className="py-3 px-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {quotations.map((q) => (
                    <tr
                      key={q.id}
                      className="border-b border-[#e5e7eb] hover:bg-[#f9fafb]"
                      data-testid={`quotation-row-${q.id}`}
                    >
                      <td className="py-3 px-4 font-normal text-[#1a1a2e]">
                        {q.quotation_number}
                      </td>
                      <td className="py-3 px-4 font-normal text-[#1a1a2e]">
                        {q.customer?.name || 'N/A'}
                      </td>
                      <td className="py-3 px-4 font-normal text-[#1a1a2e]">
                        {new Date(q.issue_date).toLocaleDateString()}
                      </td>
                      <td className="py-3 px-4 font-normal text-[#1a1a2e]">
                        {q.expiry_date ? new Date(q.expiry_date).toLocaleDateString() : '—'}
                      </td>
                      <td className="py-3 px-4 font-normal text-[#1a1a2e]">
                        Rs {parseFloat(q.total_amount || 0).toFixed(2)}
                      </td>
                      <td className="py-3 px-4">
                        <span
                          className={`px-2 py-1 rounded-sm text-xs font-medium ${statusBadge(
                            q.status
                          )}`}
                          data-testid={`status-${q.id}`}
                        >
                          {q.status}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex gap-2 flex-wrap items-center">
                          <button
                            onClick={() =>
                              window.open(`/sales/quotations/${q.id}/print`, '_blank')
                            }
                            className="flex items-center gap-1 text-[#4A7AE0] hover:bg-[#EFF6FF] border border-[#4A7AE0] rounded-lg px-2 py-0.5 text-xs font-medium transition-colors"
                            data-testid={`print-${q.id}`}
                            title="View / Print"
                          >
                            <Printer className="w-3 h-3" /> View
                          </button>

                          {q.status === 'draft' && (
                            <>
                              <button
                                onClick={() => handleSend(q)}
                                className="flex items-center gap-1 text-[#4A7AE0] hover:underline text-xs font-medium"
                                data-testid={`send-${q.id}`}
                              >
                                <Send className="w-3 h-3" /> Send
                              </button>
                              <button
                                onClick={() => handleEdit(q)}
                                className="flex items-center gap-1 text-[#6b7280] hover:underline text-xs font-medium"
                                data-testid={`edit-${q.id}`}
                              >
                                <Edit className="w-3 h-3" /> Edit
                              </button>
                              <button
                                onClick={() => handleDelete(q)}
                                className="flex items-center gap-1 text-[#EF4444] hover:bg-[#FEE2E2] border border-[#EF4444] rounded px-2 py-0.5 text-xs font-medium transition-colors"
                                data-testid={`delete-${q.id}`}
                              >
                                <Trash2 className="w-3 h-3" /> Delete
                              </button>
                            </>
                          )}

                          {q.status === 'sent' && (
                            <>
                              <button
                                onClick={() => handleAccept(q)}
                                className="flex items-center gap-1 text-[#4A7AE0] hover:underline text-xs font-medium"
                                data-testid={`accept-${q.id}`}
                              >
                                <Check className="w-3 h-3" /> Accept
                              </button>
                              <button
                                onClick={() => handleReject(q)}
                                className="flex items-center gap-1 text-[#EF4444] hover:underline text-xs font-medium"
                                data-testid={`reject-${q.id}`}
                              >
                                <XIcon className="w-3 h-3" /> Reject
                              </button>
                            </>
                          )}

                          {q.status === 'accepted' && (
                            <button
                              onClick={() => handleConvert(q)}
                              className="flex items-center gap-1 bg-gradient-to-r from-[#22B8D4] via-[#4A7AE0] to-[#7C3BD6] text-white rounded-lg px-2 py-0.5 text-xs font-medium transition-colors"
                              data-testid={`convert-${q.id}`}
                            >
                              <ArrowRight className="w-3 h-3" /> Convert to Invoice
                            </button>
                          )}

                          {q.status === 'rejected' && (
                            <button
                              onClick={() => handleDelete(q)}
                              className="flex items-center gap-1 text-[#EF4444] hover:bg-[#FEE2E2] border border-[#EF4444] rounded px-2 py-0.5 text-xs font-medium transition-colors"
                              data-testid={`delete-${q.id}`}
                            >
                              <Trash2 className="w-3 h-3" /> Delete
                            </button>
                          )}

                          {q.status === 'converted' && (
                            <button
                              onClick={() => handleViewInvoice(q)}
                              className="flex items-center gap-1 text-[#6B21A8] hover:bg-[#F3E8FF] border border-[#6B21A8] rounded px-2 py-0.5 text-xs font-medium transition-colors"
                              data-testid={`view-invoice-${q.id}`}
                            >
                              <FileText className="w-3 h-3" /> View Invoice
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </main>
      </div>

      {showForm && (
        <QuotationForm
          quotation={editingQuotation}
          onClose={handleFormClose}
          onSuccess={handleFormSuccess}
        />
      )}
    </div>
  );
};

export default Quotations;
